import React, { useEffect, useState } from "react";
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  onSnapshot,
} from "firebase/firestore";
import { db } from "../firebase/firebase";

import Sidebar from "../components/layout/Sidebar";
import Topbar from "../components/layout/Topbar";
import "./Planos.css";

export default function Planos() {
  const [planos, setPlanos] = useState([]);
  const [formVisible, setFormVisible] = useState(false);
  const [editId, setEditId] = useState(null);

  // Campos principais
  const [nome, setNome] = useState("");
  const [preco, setPreco] = useState("");
  const [duracao, setDuracao] = useState("");
  const [ativo, setAtivo] = useState(true);

  // 🔒 PERMISSÕES 100% alinhadas com o app
  const defaultPermissions = {
    ver_mensagens: false,
    ver_sugestoes: false,
    usar_tarot_avancado: false, // 3 tarots emocionais
    usar_tarot_classico: false, // Tarot Clássico (novo)
    usar_ia: false,
    ver_academia: false,
  };

  const [permissoes, setPermissoes] = useState(defaultPermissions);

  // Benefícios
  const [beneficios, setBeneficios] = useState([]);
  const [beneficioTexto, setBeneficioTexto] = useState("");

  // ==========================
  // Carregar lista de planos
  // ==========================
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "planos"), (snapshot) => {
      setPlanos(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
    });
    return () => unsub();
  }, []);

  const resetForm = () => {
    setNome("");
    setPreco("");
    setDuracao("");
    setAtivo(true);
    setBeneficios([]);
    setBeneficioTexto("");
    setPermissoes(defaultPermissions);
    setEditId(null);
  };

  // ==========================
  // Benefícios
  // ==========================
  const adicionarBeneficio = () => {
    if (!beneficioTexto.trim()) return;
    setBeneficios((prev) => [...prev, beneficioTexto.trim()]);
    setBeneficioTexto("");
  };

  const removerBeneficio = (index) => {
    setBeneficios((prev) => prev.filter((_, i) => i !== index));
  };

  // ==========================
  // Toggle de permissão
  // ==========================
  const togglePermissao = (key) => {
    setPermissoes((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  // ==========================
  // Salvar plano
  // ==========================
  const handleSave = async (e) => {
    e.preventDefault();

    const payload = {
      nome,
      preco: Number(preco),
      duracao: Number(duracao),
      ativo,
      beneficios,
      permissoes,
    };

    if (editId) {
      await updateDoc(doc(db, "planos", editId), payload);
    } else {
      await addDoc(collection(db, "planos"), payload);
    }

    resetForm();
    setFormVisible(false);
  };

  // ==========================
  // Editar plano
  // ==========================
  const handleEdit = (plano) => {
    setEditId(plano.id);
    setNome(plano.nome);
    setPreco(plano.preco);
    setDuracao(plano.duracao);
    setAtivo(plano.ativo);
    setBeneficios(plano.beneficios || []);
    setPermissoes({
      ...defaultPermissions,
      ...(plano.permissoes || {}),
    });
    setFormVisible(true);
  };

  // ==========================
  // Excluir plano
  // ==========================
  const handleDelete = async (id) => {
    await deleteDoc(doc(db, "planos", id));
  };

  return (
    <div className="app-container">
      <Sidebar />
      <div className="main-content">
        <Topbar />

        <div className="planos-container">
          <h2>Gerenciar Planos</h2>

          <button
            className="btn-add"
            onClick={() => {
              resetForm();
              setFormVisible(true);
            }}
          >
            + Novo Plano
          </button>

          {/* ==========================
              Tabela de planos
          =========================== */}
          <table className="planos-table">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Preço</th>
                <th>Duração</th>
                <th>Ativo</th>
                <th>Ações</th>
              </tr>
            </thead>

            <tbody>
              {planos.length === 0 && (
                <tr>
                  <td
                    colSpan="5"
                    style={{ textAlign: "center", padding: "20px" }}
                  >
                    Nenhum plano cadastrado ainda.
                  </td>
                </tr>
              )}

              {planos.map((plano) => (
                <tr key={plano.id}>
                  <td>{plano.nome}</td>
                  <td>R$ {Number(plano.preco).toFixed(2)}</td>
                  <td>{plano.duracao} dias</td>
                  <td>{plano.ativo ? "Sim" : "Não"}</td>
                  <td>
                    <button
                      className="btn-edit"
                      onClick={() => handleEdit(plano)}
                    >
                      Editar
                    </button>
                    <button
                      className="btn-del"
                      onClick={() => handleDelete(plano.id)}
                    >
                      Excluir
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* ==========================
              Formulário
          =========================== */}
          {formVisible && (
            <div className="form-card">
              <h3>{editId ? "Editar Plano" : "Novo Plano"}</h3>

              <form onSubmit={handleSave}>
                {/* NOME */}
                <label>Nome</label>
                <input
                  value={nome}
                  onChange={(e) => setNome(e.target.value)}
                  required
                />

                {/* PREÇO */}
                <label>Preço</label>
                <input
                  type="number"
                  step="0.01"
                  value={preco}
                  onChange={(e) => setPreco(e.target.value)}
                  required
                />

                {/* DURAÇÃO */}
                <label>Duração (dias)</label>
                <input
                  type="number"
                  value={duracao}
                  onChange={(e) => setDuracao(e.target.value)}
                  required
                />

                {/* ATIVO */}
                <label>Ativo?</label>
                <select
                  value={ativo}
                  onChange={(e) => setAtivo(e.target.value === "true")}
                >
                  <option value="true">Sim</option>
                  <option value="false">Não</option>
                </select>

                {/* PERMISSÕES */}
                <h4 className="perm-title">Permissões do Plano</h4>

                <div className="perm-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={permissoes.ver_mensagens}
                      onChange={() => togglePermissao("ver_mensagens")}
                    />
                    Liberar Mensagens (Chat)
                  </label>

                  <label>
                    <input
                      type="checkbox"
                      checked={permissoes.ver_sugestoes}
                      onChange={() => togglePermissao("ver_sugestoes")}
                    />
                    Ver Sugestões da Semana
                  </label>

                  <label>
                    <input
                      type="checkbox"
                      checked={permissoes.usar_tarot_avancado}
                      onChange={() =>
                        togglePermissao("usar_tarot_avancado")
                      }
                    />
                    Usar Tarot Avançado (Amor / Verdade / Cura)
                  </label>

                  <label>
                    <input
                      type="checkbox"
                      checked={permissoes.usar_tarot_classico}
                      onChange={() =>
                        togglePermissao("usar_tarot_classico")
                      }
                    />
                    Tarot Clássico (22 Arcanos Maiores)
                  </label>

                  <label>
                    <input
                      type="checkbox"
                      checked={permissoes.usar_ia}
                      onChange={() => togglePermissao("usar_ia")}
                    />
                    IA do Amor (Avatar / Chat Emocional)
                  </label>

                  <label>
                    <input
                      type="checkbox"
                      checked={permissoes.ver_academia}
                      onChange={() => togglePermissao("ver_academia")}
                    />
                    Academia do Amor (Aulas)
                  </label>
                </div>

                {/* BENEFÍCIOS */}
                <label>Benefícios (visuais para o app)</label>

                <div className="beneficio-add-box">
                  <input
                    type="text"
                    placeholder="Digite um benefício..."
                    value={beneficioTexto}
                    onChange={(e) => setBeneficioTexto(e.target.value)}
                  />
                  <button
                    type="button"
                    className="btn-beneficio-add"
                    onClick={adicionarBeneficio}
                  >
                    +
                  </button>
                </div>

                <ul className="beneficios-lista">
                  {beneficios.map((b, index) => (
                    <li key={index}>
                      {b}
                      <button
                        type="button"
                        className="btn-remover-beneficio"
                        onClick={() => removerBeneficio(index)}
                      >
                        Remover
                      </button>
                    </li>
                  ))}
                </ul>

                <button type="submit" className="btn-save">
                  Salvar
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
 